import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/panel";

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Hoş geldin, " + u.name);
      nav(u.role === "admin" ? "/admin" : from, { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white" data-testid="login-page">
      <div className="hidden md:flex bg-zinc-50 border-r border-zinc-200 p-10 flex-col justify-between relative overflow-hidden">
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#1E3A8A] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">SuperZeka</span>
          </Link>
          <p className="mt-2 ml-10 text-xs text-zinc-500 tracking-wide">
            Türkiye&apos;nin öğrencileri için geliştirildi.
          </p>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#1E3A8A] uppercase tracking-wider mb-4">
            <Sparkles className="w-3 h-3" />
            Tanıtım örneği
          </div>
          <h2 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight leading-snug text-zinc-900">
            &ldquo;Cevap yetmez. Neden takıldığını gördüğünde çalışman değişir.&rdquo;
          </h2>
        </div>
        <div className="relative z-10 text-xs text-zinc-500">© {new Date().getFullYear()} SuperZeka</div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-[#1E3A8A]/10 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex md:hidden items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-xl bg-[#1E3A8A] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">SuperZeka</span>
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
            Tekrar hoş geldin
          </h1>
          <p className="mt-2 text-sm text-zinc-600">Hesabınla giriş yap, kaldığın yerden devam et.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@eposta.com"
                className="rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Şifre</Label>
                <Link to="/sifremi-unuttum" className="text-xs text-[#1E3A8A] hover:underline" data-testid="forgot-link">
                  Şifremi unuttum
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-xl"
              />
            </div>

            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Giriş Yap"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-zinc-600 text-center">
            Hesabın yok mu?{" "}
            <Link to="/kayit" className="text-[#1E3A8A] font-medium hover:underline" data-testid="link-to-register">
              Ücretsiz kayıt ol
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
